import React, { useState, useEffect } from "react";
import { paragon } from "@useparagon/connect";
import { authenticateWithParagon } from "../paragonAuthHelper.js";
import ConfirmationDialog from "../components/ConfirmationDialog";
import IntegrationBox from "./IntegrationBox";
import intercom from "../integration_icons/intercom.png";

import zendesk from "../integration_icons/zendesk.png";
import gmail from "../integration_icons/gmail.png";

import chat from "../integration_icons/chat.png";
import sms from "../integration_icons/sms.png";
import wechat from "../integration_icons/wechat.png";
import outlook from "../integration_icons/ms_outlook.png";
import phone from "../integration_icons/phone.png";
import instagram from "../integration_icons/instagram.png";
import facebook from "../integration_icons/fb_messenger.png";
import ZapierTokenDialog from "./Integrations/ZapierTokenDialog";

import shopify from "../integration_icons/shopify.png";
import zapier from "../integration_icons/zapier.png";
import stripe from "../integration_icons/stripe.png";
import CenterWrapper from "../components/CenterWrapper.jsx";
import SectionHeader from "../components/SectionHeader";
import { useNavigate } from "react-router-dom";
import useFetchIntegrations from "../hooks/useFetchIntegrations";

const Integrations = () => {
  const { integrations } = useFetchIntegrations();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [isZapierDialogVisible, setIsZapierDialogVisible] = useState(false);

  const navigate = useNavigate();

  /* const integrations = [
     {
       name: "Gmail",
       description:
         "Connect your chatbot to Gmail and manage email interactions.",
       icon: <GoogleOutlined className="text-red-600 text-4xl" />,
     },
     {
       name: "Zapier",
       description: "Connect your chatbot with thousands of apps using Zapier.",
       icon: <ApiOutlined className="text-orange-500 text-4xl" />,
       comingSoon: true, // Zapier is marked as coming soon
     },
     {
       name: "Shopify",
       description:
         "Integrate your chatbot with Shopify to manage orders and customer inquiries.",
       icon: <ShopOutlined className="text-green-500 text-4xl" />,
       comingSoon: true, // Shopify is marked as coming soon
     },
   ];*/

  const CRM = [
    {
      name: "Intercom",
      description:
        "Have your AI Agent respond to customer inquiries in Intercom.",
      icon: intercom,
      comingSoon: true,
      connected: false,
    },
    {
      name: "Zendesk",
      description:
        "Have your AI Agent respond to customer inquiries in Zendesk.",
      icon: zendesk,
      comingSoon: true,
      connected: false,
    },
  ];

  const COMMUNICATIONS = [
    {
      name: "Livechat",
      description:
        "Use livechat to communicate with customers on your website.",
      icon: chat,
      comingSoon: false,
      connected: false,
      href: "/setup/deploy",
    },
    {
      name: "Gmail",
      description:
        "Connect your support Gmail inbox to have your AI Agent handle customer inquiries.",
      icon: gmail,
      comingSoon: true,
      connected: false,
    },
    {
      name: "Outlook",
      description:
        "Connect your support Outlook inbox to have your AI Agent handle customer inquiries.",
      icon: outlook,
      comingSoon: true,
      connected: false,
    },
    {
      name: "Twilio SMS",
      description:
        "Connect your Twilio account to have your AI Agent handle 2-way SMS customer inquiries.",
      icon: sms,
      comingSoon: true,
      connected: false,
    },
    {
      name: "Twilio Phone",
      description:
        "Connect your Twilio account to have your AI Agent handle 2-way phone calls.",
      icon: phone,
      comingSoon: true,
      connected: false,
    },
    {
      name: "Instagram DMs",
      description:
        "Connect your Instagram to have your AI Agent handle customer DMs.",
      icon: instagram,
      comingSoon: true,
      connected: false,
    },
    {
      name: "Facebook Messenger",
      description:
        "Connect your Facebook Business Account to have your AI Agent handle your customer inquiries.",
      icon: facebook,
      comingSoon: true,
      connected: false,
    },
    {
      name: "WhatsApp",
      description:
        "Connect your Whatsapp Business Account to have your AI Agent handle your customer inquiries.",
      icon: wechat,
      comingSoon: true,
      connected: false,
    },
  ];

  const DATA_AND_ACTIONS = [
    {
      name: "Zapier",
      description:
        "Connect your Zapier account to have your AI agent handle actions with your other apps based on Customer Intents within conversations.",
      icon: zapier,
      comingSoon: false,
      connected: integrations.find(i => i.integration_type === "ZAPIER"),
    },
    {
      name: "Shopify",
      description:
        "Connect your Shopify account to have your AI agent handle actions based on Customer Intents within conversations.",
      icon: shopify,
      comingSoon: true,
      connected: false,
    },
    {
      name: "Stripe",
      description:
        "Connect your Stripe account to have your AI agent handle actions based on Customer Intents within conversations.",
      icon: stripe,
      comingSoon: true,
      connected: false,
    },
  ];

  const handleCardClick = integration => {
    if (!integration.comingSoon) {
      if (integration.href) {
        navigate(integration.href);
      } else {
        setSelectedIntegration(integration);
        if (integration.name === "Zapier") {
          setIsZapierDialogVisible(true);
        } else {
          setIsModalVisible(true);
        }
      }
    }
  };

  const handleOk = () => {
    setIsModalVisible(false);
    const companyId = localStorage.getItem("chatbot_uuid");

    if (!companyId) {
      console.error("Company ID not found in sessionStorage");
      return;
    }

    authenticateWithParagon(companyId)
      .then(() => {
        paragon.connect("gmail", {
          onSuccess: () => {
            console.log("Connected to Gmail successfully");
          },
          onError: error => {
            console.error("Error connecting to Gmail:", error);
          },
        });
      })
      .catch(error => {
        console.error("Error authenticating with Paragon:", error);
      });
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  return (
    <CenterWrapper>
      <div>
        <div className="pb-6 border-b">
          <SectionHeader
            title="Integrations"
            description="Utilize integrations to communicate through all your channels,
            enable your AI agent to take actions and pull in data from your
            backend to provide excellent customer service and upselling
            capability."
          />
        </div>

        <div className="my-12">
          <SectionHeader
            title="Connect your CRM"
            description="Connect your CRM to escalate handed-off tickets (coming soon) and
            have your AI Agent respond to tickets through your CRM instead of
            utilizing SupportMagic (coming soon)."
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12 pb-6 border-b">
            {CRM.map(integration => (
              <IntegrationBox
                integration={integration}
                handleCardClick={handleCardClick}
              />
            ))}
          </div>
        </div>

        <section className="mb-12">
          <SectionHeader
            title="Communication Channels"
            description="Connect your business communication channels to allow your AI to
            handle customer requests everywhere you receive them."
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12 pb-6 border-b">
            {COMMUNICATIONS.map(integration => (
              <IntegrationBox
                integration={integration}
                handleCardClick={handleCardClick}
              />
            ))}
          </div>
        </section>

        <section className="mb-12">
          <SectionHeader
            title="Data & Actions"
            description="Connect your tools to automate your support end to end. When you AI
            recognizes a customer intent, it can take action on your behalf."
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
            {DATA_AND_ACTIONS.map(integration => (
              <IntegrationBox
                integration={integration}
                handleCardClick={handleCardClick}
              />
            ))}
          </div>
        </section>

        {/* Modal */}
        {isModalVisible && selectedIntegration?.name !== "Zapier" && (
          <ConfirmationDialog
            isVisible={isModalVisible}
            onClose={handleCancel}
            onCancel={handleCancel}
            onConfirm={handleOk}
            title={`Enable ${selectedIntegration?.name}`}
            description={`Are you sure you want to enable the ${selectedIntegration?.name} integration?`}
            confirmLabel="Enable"
            cancelLabel="Cancel"
            useCase="positive"
          />
        )}

        {isZapierDialogVisible && (
          <ZapierTokenDialog
            isVisible={isZapierDialogVisible}
            onClose={() => setIsZapierDialogVisible(false)}
            integration={integrations.find(i => i.integration_type === "ZAPIER")}
          />
        )}
      </div>
    </CenterWrapper>
  );
};

export default Integrations;
